import React, { useEffect, useMemo, useState } from "react";
import {
  HelpCircle,
  Search,
  Plus,
  Minus,
  ArrowRight,
  ShieldCheck,
  Zap,
  Sparkles,
  Users,
  MessageSquare,
  Lock,
  Globe,
  Settings,
  Mail
} from "lucide-react";
import "../assets/landing.css";
import MarketingHeader from "../components/MarketingHeader";
import MarketingFooter from "../components/MarketingFooter";
import MarketingFAQ from "../components/MarketingFAQ";
import { marketingNavLinks } from "../constants/marketingNav";

const FAQPage = () => {
  const [searchQuery, setSearchQuery] = useState("");

  const navLinks = useMemo(
    () =>
      marketingNavLinks.map((link) => {
        if (link.label === "TeamBoard") return { ...link, route: "/" };
        if (link.label === "Features") return { ...link, route: "/features" };
        if (link.label === "Why TeamBoard") return { ...link, route: "/why-teamboard" };
        if (link.label === "Pricing") return { ...link, route: "/pricing" };
        if (link.label === "Testimonials") return { ...link, route: "/testimonials" };
        if (link.label === "FAQ") return { ...link, route: "/faq" };
        if (link.label === "Contact Us") return { ...link, route: "/contact" };
        return link;
      }),
    []
  );

  useEffect(() => {
    const revealElements = document.querySelectorAll(
      ".landing-page .reveal, .landing-page .section-animate"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page font-sans antialiased text-gray-900 bg-white">
      <MarketingHeader navLinks={navLinks} ctaTarget="contact" />

      <main>
        <section className="curved-section shell-sky section-animate pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="section-eyebrow reveal">
              <HelpCircle className="w-4 h-4 inline mr-2" />
              Help Center
            </span>
            <h1 className="reveal stagger-1 mt-6 mb-8 text-gray-900">
              Common <span className="text-blue-600">Questions</span>
            </h1>
            <p className="text-lead reveal stagger-2 max-w-2xl mx-auto mb-12">
              Everything you need to know about PMS. Can't find what you're looking for? 
              Reach out to our support team.
            </p>

            <div className="max-w-2xl mx-auto relative reveal stagger-3">
              <div className="absolute inset-y-0 left-5 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="w-full pl-14 pr-6 py-5 bg-white border border-gray-200 rounded-2xl shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-lg"
                placeholder="Search for answers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </section>

        <MarketingFAQ />

        <section className="shell-navy text-white text-center">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="w-20 h-20 bg-white/10 rounded-[30px] flex items-center justify-center mx-auto mb-8">
              <MessageSquare className="w-10 h-10 text-sky" />
            </div>
            <h2 className="mb-6">Still have questions?</h2>
            <p className="text-lead text-blue-100 mb-10">
              We're here to help. Our support team is available 24/7 to answer any 
              questions you might have about PMS.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="mailto:support@pms.example.com"
                className="btn-secondary"
              >
                <Mail className="w-5 h-5 mr-2" />
                Contact Support
              </a>
              <button className="btn-secondary !bg-transparent !text-white !border-white/20 hover:!bg-white/5">
                Schedule a Demo
              </button>
            </div>
          </div>
        </section>

        <section className="shell-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 reveal">
              <h2 className="mb-4 text-navy">Additional Resources</h2>
              <p className="text-body">Explore our documentation and guides to master PMS.</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { icon: Lock, title: "Security Whitepaper", desc: "Learn more about how we protect your sensitive data and vault credentials." },
                { icon: Users, title: "Community Forum", desc: "Join thousands of users sharing tips and best practices for PMS." },
                { icon: Settings, title: "API Documentation", desc: "Everything you need to know about building custom integrations." }
              ].map((item, idx) => (
                <div key={idx} className="card-marketing card-hover reveal stagger-1">
                  <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-6">
                    <item.icon className="w-7 h-7 text-navy" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{item.title}</h3>
                  <p className="text-gray-600 mb-6">{item.desc}</p>
                  <button className="flex items-center gap-2 text-blue-600 font-bold hover:gap-3 transition-all">
                    Learn More
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
};

export default FAQPage;
